// A permalink into a comment (`#kommentar-42` — cut reports, admin reports)
// can point at a row that lives inside a `<details>` closed by default (the
// reply thread under a cut report, `cut_reports/_comments.html.erb`). The
// browser's own #hash jump scrolls straight past it: closed `<details>`
// content has no box to land on. This module does what the jump alone
// cannot — find the target, open every `<details>` that encloses it (there
// can be more than one, nested), and only then scroll it into view — once on
// page load and again on every `hashchange` (a click on another comment's
// permalink while the page is already open).
//
// A plain module, not a React component, started from
// `entrypoints/application.ts` the same way the confirmation dialogs and the
// copy buttons are: no inline `onhashchange=` — CSP's `script-src` carries
// no `'unsafe-inline'` (`app/frontend/ui/confirmForm.ts`).

const COMMENT_HASH_PREFIX = '#kommentar-'

/**
 * Opens every closed `<details>` between `target` and the document root, so
 * an element that starts hidden inside one — or several, nested — has a box
 * to be part of once this returns.
 */
export function openEnclosingDetails(target: Element): void {
  let details = target.closest('details')

  while (details) {
    if (details instanceof HTMLDetailsElement) details.open = true
    details = details.parentElement?.closest('details') ?? null
  }
}

/**
 * The one entry point both the initial load and a later `hashchange` call
 * through: nothing for a hash this module has no business opening anything
 * for, and nothing for a hash whose target the page does not have.
 */
export function openHashTarget(hash: string, root: ParentNode = document): void {
  if (!hash.startsWith(COMMENT_HASH_PREFIX)) return
  // The bare prefix names no comment — nothing to look for.
  if (hash === COMMENT_HASH_PREFIX) return

  // An attribute selector rather than `#id` plus `CSS.escape`: the id is
  // always `kommentar-<numeric id>` (`cut_report_comments/_comment.html.erb`)
  // and never needs escaping, and not every `ParentNode` this runs against
  // in a test is a `Document` with a `CSS` global to escape with.
  const target = root.querySelector(`[id="${hash.slice(1)}"]`)
  if (!target) return

  openEnclosingDetails(target)
  target.scrollIntoView()
}

export function startAnchorDetails(win: Window = window): void {
  const applyHash = () => openHashTarget(win.location.hash, win.document)

  applyHash()
  win.addEventListener('hashchange', applyHash)
}
